import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  DatePicker,
  Typography,
  Space,
  message,
  Spin,
  Carousel,
  Select,
  Tag,
  Statistic,
  Modal,
  Table,
  Tooltip
} from 'antd';
import { StockOutlined, LeftOutlined, RightOutlined, ReloadOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import StockChart from '../components/StockChart';
import StockAnalysisModal from '../components/StockAnalysisModal';
import { getStockInfo, getAllStocks, addStockFromSearch } from '../services/stockService';
import { searchStocksWithGemini } from '../services/geminiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Stocks = () => {
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, 'days'),
    moment()
  ]);
  const [stocksData, setStocksData] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableStocks, setAvailableStocks] = useState([]);
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [carouselRef, setCarouselRef] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadingStocks, setLoadingStocks] = useState(false);
  
  // AI 분석 모달 관련 state
  const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
  const [selectedStockForAnalysis, setSelectedStockForAnalysis] = useState(null);
  
  // 주식 검색 및 추가 모달 관련 state
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingStock, setAddingStock] = useState(false);

  // 사용 가능한 주식 목록을 DB에서 가져오는 함수
  const loadAvailableStocks = async () => {
    setLoadingStocks(true);
    try {
      const response = await getAllStocks();
      
      if (response && response.results) {
        // getAllStocks API 응답을 적절한 형식으로 변환
        const formattedStocks = response.results.map(stock => ({
          id: stock.id,
          code: stock.stock_symbol,
          name: stock.stock_name,
          sector: stock.sector || 'Unknown',
          data_count: 0 // getAllStocks는 data_count를 제공하지 않음
        }));
        
        setAvailableStocks(formattedStocks);
        
        // 처음 로드 시에는 아무것도 선택하지 않음 - 사용자가 직접 선택하도록 함
        // 기존에 선택된 종목이 없을 때만 빈 배열로 설정
        if (selectedStocks.length === 0) {
          setSelectedStocks([]);
        }
        
        message.success(`Loaded ${formattedStocks.length} stocks from database. Please select stocks to analyze.`);
      }
    } catch (error) {
      console.error('Failed to fetch available stocks', error);
      message.error('Failed to load available stocks from database');
    } finally {
      setLoadingStocks(false);
    }
  };

  // 선택된 주식들의 데이터를 가져오는 함수
  const loadStocksData = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error('Please select date range');
      return;
    }

    setLoading(true);
    const startDate = dateRange[0].format('YYYY-MM-DD');
    const endDate = dateRange[1].format('YYYY-MM-DD');
    
    try {
      const newStocksData = {};
      
      // 병렬로 모든 주식 데이터 가져오기
      const promises = selectedStocks.map(async (stock) => {
        try {
          const data = await getStockInfo(stock.code, startDate, endDate);
          return { code: stock.code, data };
        } catch (error) {
          console.error(`Failed to fetch data for ${stock.code}:`, error);
          return { code: stock.code, data: [] };
        }
      });

      const results = await Promise.all(promises);
      
      results.forEach(({ code, data }) => {
        newStocksData[code] = data;
      });

      setStocksData(newStocksData);
      message.success(`Loaded data for ${selectedStocks.length} stocks`);
    } catch (error) {
      console.error('Failed to fetch stocks data', error);
      message.error('Failed to fetch stocks data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 사용 가능한 주식 목록 로드
  useEffect(() => {
    loadAvailableStocks();
  }, []);

  // 선택된 주식이 변경될 때마다 차트 데이터 로드
  useEffect(() => {
    if (selectedStocks.length > 0) {
      loadStocksData();
    }
  }, [selectedStocks]);

  // 주식 선택 변경 시
  const handleStockSelectionChange = (stockCodes) => {
    const newSelectedStocks = availableStocks.filter(stock => 
      stockCodes.includes(stock.code)
    );
    setSelectedStocks(newSelectedStocks);
  };

  // Carousel 네비게이션
  const goToPrev = () => {
    carouselRef?.prev();
  };

  const goToNext = () => {
    carouselRef?.next();
  };

  const goToSlide = (slideIndex) => {
    carouselRef?.goTo(slideIndex);
  };

  // AI 분석 모달 열기
  const handleOpenAIAnalysis = (stock) => {
    const stockData = stocksData[stock.code];
    const latestData = stockData && stockData.length > 0 ? stockData[stockData.length - 1] : null;
    
    if (!latestData) {
      message.warning('No price data available for analysis');
      return;
    }

    // 전일대비 계산
    let prevDayChange = 0;
    let prevDayChangePercent = 0;
    
    if (stockData.length >= 2) {
      const prevData = stockData[stockData.length - 2];
      prevDayChange = latestData.close - prevData.close;
      prevDayChangePercent = ((prevDayChange / prevData.close) * 100);
    }

    setSelectedStockForAnalysis({
      id: stock.id,
      code: stock.code,
      name: stock.name,
      current_price: latestData.close,
      prev_day_change: prevDayChange,
      prev_day_change_percent: prevDayChangePercent
    });
    setAnalysisModalVisible(true);
  };

  // AI 분석 모달 닫기
  const handleCloseAIAnalysis = () => {
    setAnalysisModalVisible(false);
    setSelectedStockForAnalysis(null);
  };

  // 주식 검색 모달 열기
  const handleOpenSearchModal = () => {
    setSearchModalVisible(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  // 주식 검색 모달 닫기
  const handleCloseSearchModal = () => {
    setSearchModalVisible(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  // 주식 검색 (Gemini API 사용)
  const handleSearchStocks = async () => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      message.warning('Please enter at least 2 characters to search');
      return;
    }

    setSearching(true);
    try {
      const response = await searchStocksWithGemini(searchQuery.trim(), 20);
      
      if (response.success && response.results && response.results.length > 0) {
        setSearchResults(response.results);
        message.success(`Found ${response.results.length} stocks via Gemini AI`);
      } else {
        setSearchResults([]);
        message.info('No stocks found');
      }
    } catch (error) {
      console.error('Failed to search stocks:', error);
      message.error('Failed to search stocks. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // 주식 추가
  const handleAddStock = async (stock) => {
    setAddingStock(true);
    try {
      const response = await addStockFromSearch(stock.code, stock.name, false);
      
      if (response.already_exists) {
        message.info(`${stock.name} (${stock.code}) already exists in database`);
      } else {
        message.success(`${stock.name} (${stock.code}) added successfully!`);
        // 주식 목록 새로고침
        await loadAvailableStocks();
      }
      
      handleCloseSearchModal();
    } catch (error) {
      console.error('Failed to add stock:', error);
      message.error('Failed to add stock. Please try again.');
    } finally {
      setAddingStock(false);
    }
  };

  // 검색 결과 테이블 컬럼 정의
  const searchColumns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Market',
      dataIndex: 'market',
      key: 'market',
      width: 100,
    },
    {
      title: 'Close Price',
      dataIndex: 'close',
      key: 'close',
      width: 120,
      render: (value) => value ? `₩${value.toLocaleString()}` : '-'
    },
    {
      title: 'Change',
      dataIndex: 'change_ratio',
      key: 'change_ratio',
      width: 100,
      render: (value) => {
        if (!value) return '-';
        const color = value >= 0 ? '#52c41a' : '#f5222d';
        return <span style={{ color }}>{value >= 0 ? '+' : ''}{value.toFixed(2)}%</span>;
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button 
          type="primary" 
          size="small"
          icon={<PlusOutlined />}
          onClick={() => handleAddStock(record)}
          loading={addingStock}
        >
          Add
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        {/* Header and Controls */}
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <StockOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#1890ff' }} />
                <Title level={2} style={{ display: 'inline', margin: 0 }}>
                  Stock Analysis Carousel
                </Title>
              </div>
              
              <Row gutter={[16, 16]} align="top">
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Text strong>Select Stocks</Text>
                    <Select
                      mode="multiple"
                      placeholder={loadingStocks ? "Loading stocks..." : "Choose stocks to analyze"}
                      value={selectedStocks.map(stock => stock.code)}
                      onChange={handleStockSelectionChange}
                      style={{ width: '100%', marginTop: '4px' }}
                      maxTagCount={3}
                      loading={loadingStocks}
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {availableStocks.map(stock => (
                        <Option key={stock.code} value={stock.code} label={`${stock.name} ${stock.code} ${stock.sector}`}>
                          <Space>
                            <span>{stock.name}</span>
                            <Tag color="blue" size="small">{stock.sector}</Tag>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              ({stock.data_count} records)
                            </Text>
                          </Space>
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                
                <Col xs={24} sm={8} md={8}>
                  <div>
                    <Text strong>Date Range</Text>
                    <RangePicker
                      value={dateRange}
                      onChange={setDateRange}
                      format="YYYY-MM-DD"
                      style={{ width: '100%', marginTop: '4px' }}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <Space size="small" wrap>
                        <Button 
                          size="small" 
                          onClick={() => setDateRange([moment().startOf('week'), moment()])}
                        >
                          This Week
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => setDateRange([moment().subtract(1, 'week'), moment()])}
                        >
                          1W
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => setDateRange([moment().subtract(1, 'month'), moment()])}
                        >
                          1M
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => setDateRange([moment().subtract(3, 'months'), moment()])}
                        >
                          3M
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => setDateRange([moment().subtract(6, 'months'), moment()])}
                        >
                          6M
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => setDateRange([moment().subtract(1, 'year'), moment()])}
                        >
                          1Y
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => setDateRange([moment().subtract(30, 'days'), moment()])}
                          type="dashed"
                        >
                          Clear
                        </Button>
                      </Space>
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={4} md={8}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={loadStocksData}
                      loading={loading}
                      disabled={selectedStocks.length === 0}
                      style={{ marginTop: '20px', width: '100%' }}
                    >
                      Load Chart Data
                    </Button>
                    <Space style={{ width: '100%' }}>
                      <Button
                        type="default"
                        icon={<ReloadOutlined />}
                        onClick={loadAvailableStocks}
                        loading={loadingStocks}
                        size="small"
                        style={{ flex: 1 }}
                      >
                        Refresh List
                      </Button>
                      <Tooltip title="Search and add new stocks to database">
                        <Button
                          type="dashed"
                          icon={<SearchOutlined />}
                          onClick={handleOpenSearchModal}
                          size="small"
                        >
                          Add Stock
                        </Button>
                      </Tooltip>
                    </Space>
                  </Space>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>

        {/* Stock Summary Cards */}
        <Col span={24}>
          <Card title="Selected Stocks Overview" size="small">
            <Row gutter={[16, 16]}>
              {selectedStocks.map((stock, index) => (
                <Col key={stock.code} xs={24} sm={12} md={8} lg={6}>
                  <Card 
                    size="small" 
                    hoverable
                    onClick={() => goToSlide(index)}
                    style={{ 
                      cursor: 'pointer',
                      border: currentSlide === index ? '2px solid #1890ff' : '1px solid #d9d9d9'
                    }}
                  >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>{stock.name}</Text>
                        <Tag color="blue">{stock.sector}</Tag>
                      </div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {stock.code}
                      </Text>
                      {stocksData[stock.code] && stocksData[stock.code].length > 0 && (
                        <Statistic
                          title="Latest Close"
                          value={stocksData[stock.code][stocksData[stock.code].length - 1]?.close}
                          precision={0}
                          suffix="₩"
                          valueStyle={{ fontSize: '14px' }}
                        />
                      )}
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        {/* Carousel Navigation */}
        <Col span={24}>
          <Card size="small">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button 
                icon={<LeftOutlined />} 
                onClick={goToPrev}
                disabled={loading || selectedStocks.length === 0}
              >
                Previous
              </Button>
              
              <Space>
                <Text strong>
                  {selectedStocks.length > 0 ? 
                    `${currentSlide + 1} / ${selectedStocks.length}` : 
                    'No stocks selected'
                  }
                </Text>
                {selectedStocks.length > 0 && (
                  <Text type="secondary">
                    - {selectedStocks[currentSlide]?.name}
                  </Text>
                )}
              </Space>
              
              <Button 
                icon={<RightOutlined />} 
                onClick={goToNext}
                disabled={loading || selectedStocks.length === 0}
              >
                Next
              </Button>
            </div>
          </Card>
        </Col>

        {/* Carousel Content */}
        <Col span={24}>
          {loading ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                  <Text>Loading stock data for {selectedStocks.length} stocks...</Text>
                </div>
              </div>
            </Card>
          ) : selectedStocks.length === 0 ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '100px' }}>
                <StockOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Title level={4} type="secondary">No stocks selected</Title>
                <Text type="secondary">Please select stocks from the dropdown above to view their analysis.</Text>
              </div>
            </Card>
          ) : (
            <Carousel
              ref={setCarouselRef}
              afterChange={setCurrentSlide}
              dots={false}
              infinite={false}
            >
              {selectedStocks.map((stock) => (
                <div key={stock.code}>
                  <StockChart 
                    stockData={stocksData[stock.code] || []} 
                    stockCode={stock.code}
                    stockName={stock.name}
                    stockId={stock.id}
                    onAIAnalysis={() => handleOpenAIAnalysis(stock)}
                  />
                </div>
              ))}
            </Carousel>
          )}
        </Col>
      </Row>

      {/* AI 분석 모달 */}
      <StockAnalysisModal
        visible={analysisModalVisible}
        selectedStock={selectedStockForAnalysis}
        onClose={handleCloseAIAnalysis}
      />

      {/* 주식 검색 및 추가 모달 */}
      <Modal
        title={
          <Space>
            <SearchOutlined style={{ color: '#1890ff' }} />
            <span>Search and Add Stocks</span>
          </Space>
        }
        visible={searchModalVisible}
        onCancel={handleCloseSearchModal}
        footer={null}
        width={800}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Search for stocks by name or code</Text>
            <Space.Compact style={{ width: '100%', marginTop: '8px' }}>
              <Input
                placeholder="Enter stock name or code (e.g., Samsung, 005930)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onPressEnter={handleSearchStocks}
                disabled={searching}
              />
              <Button 
                type="primary" 
                icon={<SearchOutlined />}
                onClick={handleSearchStocks}
                loading={searching}
              >
                Search
              </Button>
            </Space.Compact>
          </div>

          {searching ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text>Searching stocks...</Text>
              </div>
            </div>
          ) : searchResults.length > 0 ? (
            <div>
              <Text strong>Search Results ({searchResults.length})</Text>
              <Table
                columns={searchColumns}
                dataSource={searchResults}
                rowKey="code"
                pagination={{ pageSize: 10 }}
                size="small"
                style={{ marginTop: '8px' }}
                scroll={{ x: 700 }}
              />
            </div>
          ) : searchQuery && !searching ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text type="secondary">No results found. Try a different search term.</Text>
            </div>
          ) : null}
        </Space>
      </Modal>
    </div>
  );
};

export default Stocks;
